import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Select, { components } from "react-select";
import { get, patch } from "../../utils/fetchAPI";
import { toast } from "react-toastify";
import Loader from "../../components/loader/loder";
import {
    FaFileInvoiceDollar,
    FaArrowLeft,
    FaHashtag,
    FaUser,
    FaEnvelope,
    FaPhone,
    FaMapMarkerAlt,
    FaBoxOpen,
    FaTrashAlt,
    FaSave,
} from "react-icons/fa";

// Shared field styling so every input/select looks the same across the form.
const inputClass =
    "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none transition";

const selectStyles = {
    control: (base, state) => ({
        ...base,
        borderRadius: "0.5rem",
        borderColor: state.isFocused ? "#14b8a6" : "#d1d5db",
        boxShadow: state.isFocused ? "0 0 0 2px rgba(20,184,166,0.20)" : "none",
        minHeight: "42px",
        "&:hover": { borderColor: "#14b8a6" },
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? "#14b8a6" : state.isFocused ? "#f0fdfa" : "#fff",
        color: state.isSelected ? "#fff" : "#1f2937",
        cursor: "pointer",
    }),
    menu: (base) => ({ ...base, borderRadius: "0.5rem", overflow: "hidden", zIndex: 30 }),
};

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
    <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
        <div className="w-9 h-9 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
            <Icon className="text-lg" />
        </div>
        <div>
            <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
            {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
    </div>
);

const billingFields = [
    { key: "name", label: "Name", icon: FaUser, full: false },
    { key: "email", label: "Email", icon: FaEnvelope, full: false },
    { key: "phone", label: "Phone", icon: FaPhone, full: false },
    { key: "address", label: "Address", icon: FaMapMarkerAlt, full: true },
];

const OrderUpdate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [items, setItems] = useState([]);
    const [selectedItemOption, setSelectedItemOption] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [formData, setFormData] = useState({
        billTo: { name: "", email: "", phone: "", address: "" },
        customInvoiceNumber: "",
        add: [],
        remove: [],
        itemUpdates: {}, // { [existingItemId]: description }
    });

    // 🧠 Load order details
    useEffect(() => {
        fetchOrderDetails();
    }, []);

    const fetchOrderDetails = async () => {
        try {
            const res = await get(`/order/${id}`);
            const orderData = res?.data;

            setOrder(orderData);
            setFormData({
                billTo: orderData?.billTo || {
                    name: "",
                    email: "",
                    phone: "",
                    address: "",
                },
                // Default the custom invoice to the actual invoice number; the user can change it.
                customInvoiceNumber:
                    orderData?.customInvoiceNumber || String(orderData?.orderNumber || ""),
                add: [],
                remove: [],
                itemUpdates: {},
            });

            // Fetch catalog items
            const catalogRes = await get(
                `/order/catalog?organization=${orderData.organization?._id}&doctor=${orderData.doctor?._id}`
            );
            setItems(catalogRes?.data?.items || []);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load order");
        }
    };

    // Check if item code already exists in current items (not removed)
    const isItemInCurrentItems = (itemCode) => {
        if (!order?.orderItems) return false;
        const currentItems = order.orderItems.filter(item => !formData.remove.includes(item._id));
        return currentItems.some(item => {
            // Check by code or name match
            const catalogItem = items.find(i => i.code === itemCode);
            return item.code === itemCode || (catalogItem && item.name === catalogItem.name);
        });
    };

    // Check if item already added to new items
    const isItemInNewItems = (itemCode) => {
        return formData.add.some(item => item.code === itemCode);
    };

    // ➕ Handle item selection from dropdown
    const handleItemSelect = (selectedItem) => {
        if (!selectedItem) return;

        const itemCode = selectedItem.code || selectedItem.value;

        // If item already exists in current items (not removed), show message
        if (isItemInCurrentItems(itemCode)) {
            toast.info("This item is already in the order. You can remove it using the Remove button.");
            setSelectedItemOption(null);
            return;
        }

        // If item already in new items list, show message
        if (isItemInNewItems(itemCode)) {
            toast.info("This item is already added. You can remove it using the Remove button.");
            setSelectedItemOption(null);
            return;
        }

        // Add as new item with full editing options
        const newItem = {
            code: itemCode,
            quantity: 1,
            discount: "0.00",
            description: "",
        };

        setFormData({
            ...formData,
            add: [...formData.add, newItem],
        });

        setSelectedItemOption(null); // Reset dropdown
    };

    // 🗑 Remove existing item (from current items)
    const handleRemoveCurrentItem = (itemId) => {
        setFormData({
            ...formData,
            remove: [...formData.remove, itemId],
        });
    };

    // 🗑 Remove new item (from add list)
    const handleRemoveNewItem = (index) => {
        const updatedAdd = formData.add.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            add: updatedAdd,
        });
    };

    // 📝 Update new item fields
    const handleNewItemChange = (index, field, value) => {
        const updated = [...formData.add];
        updated[index][field] = value;
        setFormData({ ...formData, add: updated });
    };

    // 📝 Edit an existing item's description
    const handleExistingDescriptionChange = (itemId, value) => {
        setFormData((prev) => ({
            ...prev,
            itemUpdates: { ...prev.itemUpdates, [itemId]: value },
        }));
    };

    // 🧾 Update billTo info
    const handleBillToChange = (field, value) => {
        setFormData({
            ...formData,
            billTo: { ...formData.billTo, [field]: value },
        });
    };

    // ✅ Submit PATCH
    const handleSubmit = async () => {
        setIsUpdating(true);
        try {
            const payload = {
                billTo: formData.billTo,
                add: formData.add,
                remove: formData.remove,
                customInvoiceNumber: formData.customInvoiceNumber?.trim() || "",
                itemUpdates: Object.entries(formData.itemUpdates).map(
                    ([itemId, description]) => ({ _id: itemId, description })
                ),
            };
            await patch(`/order/${id}`, payload);
            toast.success("Order updated successfully!");
            await fetchOrderDetails(); // refresh data
        } catch (err) {
            console.error(err);
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Update failed!";
            toast.error(msg);
        } finally {
            setIsUpdating(false);
        }
    };

    const CustomOption = (props) => (
        <components.Option {...props}>
            <div className="flex flex-col">
                <span className="font-medium text-gray-800">{props.data.name}</span>
                <span className="text-xs text-gray-500">
                    Rate: ৳{props.data.unitPrice.toFixed(2)}
                </span>
            </div>
        </components.Option>
    );

    if (!order) return <div className="p-10 flex justify-center"><Loader /></div>;

    // Get current items (not removed)
    const currentItems = order?.orderItems?.filter(item => !formData.remove.includes(item._id)) || [];
    const newItems = formData.add || [];
    const allItemsCount = currentItems.length + newItems.length;

    // Get item options for dropdown (exclude already added items)
    const itemOptions = items
        .filter(item => {
            const code = item.code;
            return !isItemInCurrentItems(code) && !isItemInNewItems(code);
        })
        .map(it => ({
            value: it.code,
            label: it.name,
            name: it.name,
            unitPrice: Number(it.unitPrice) || 0,
            code: it.code,
        }));

    // Calculate unified summary
    const calculateSummary = () => {
        let totalQuantity = 0;
        let subTotal = 0;
        let totalDiscount = 0;
        let grandTotal = 0;

        // Add current items
        currentItems.forEach(item => {
            const qty = Number(item.quantity) || 0;
            const rate = Number(item.unitPrice || item.rate) || 0;
            const discount = Number(item.discount) || 0;
            const itemTotal = qty * rate;
            totalQuantity += qty;
            subTotal += itemTotal;
            totalDiscount += discount;
            grandTotal += itemTotal - discount;
        });

        // Add new items
        newItems.forEach(it => {
            const itemData = items.find(x => x.code === it.code);
            const qty = Number(it.quantity) || 0;
            const rate = Number(itemData?.unitPrice) || 0;
            const discount = Number(it.discount) || 0;
            const itemTotal = qty * rate;
            totalQuantity += qty;
            subTotal += itemTotal;
            totalDiscount += discount;
            grandTotal += itemTotal - discount;
        });

        return { totalQuantity, subTotal, totalDiscount, grandTotal };
    };

    const summary = calculateSummary();

    return (
        <div className="max-w-5xl mx-auto pb-10">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-600 to-blue-500 rounded-xl px-6 py-5 mb-6 shadow-md flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                        <FaFileInvoiceDollar className="text-white text-2xl" />
                    </div>
                    <div>
                        <h1 className="text-white text-xl font-bold leading-tight">Update Order</h1>
                        <div className="flex flex-wrap items-center gap-2 mt-1.5">
                            <span className="text-white text-xs font-medium bg-white/20 rounded px-2 py-0.5">
                                Invoice #{order.orderNumber}
                            </span>
                            {order.status && (
                                <span className="text-white text-xs font-medium bg-white/20 rounded px-2 py-0.5 capitalize">
                                    {order.status}
                                </span>
                            )}
                            {order.paymentStatus && (
                                <span className="text-white text-xs font-medium bg-white/20 rounded px-2 py-0.5 capitalize">
                                    {order.paymentStatus}
                                </span>
                            )}
                        </div>
                        {(order.createdBy?.name || order.updatedBy?.name) && (
                            <p className="text-white/80 text-xs mt-1.5">
                                {order.createdBy?.name && (
                                    <>Created by <span className="font-semibold text-white">{order.createdBy.name}</span></>
                                )}
                                {order.updatedBy?.name && String(order.updatedBy?.id) !== String(order.createdBy?.id) && (
                                    <>{order.createdBy?.name ? " · " : ""}Last updated by <span className="font-semibold text-white">{order.updatedBy.name}</span></>
                                )}
                            </p>
                        )}
                    </div>
                </div>
                <button
                    onClick={() => navigate("/order-list")}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 text-white text-sm font-medium rounded-lg transition shrink-0"
                >
                    <FaArrowLeft />
                    <span className="hidden sm:inline">Back to List</span>
                </button>
            </div>

            <div className="space-y-6">
                {/* Invoice details */}
                <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    <SectionHeader icon={FaHashtag} title="Invoice Details" subtitle="Reference number for this invoice" />
                    <div className="max-w-md">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Custom Invoice Number</label>
                        <div className="relative">
                            <FaHashtag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                            <input
                                type="text"
                                className={`${inputClass} pl-9`}
                                placeholder="e.g. INV-2026-001"
                                value={formData.customInvoiceNumber}
                                onChange={(e) =>
                                    setFormData({ ...formData, customInvoiceNumber: e.target.value })
                                }
                            />
                        </div>
                        <p className="text-xs text-gray-400 mt-1.5">
                            Defaults to the actual invoice number (#{order.orderNumber}). Change it if needed.
                        </p>
                    </div>
                </section>

                {/* Bill To */}
                <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    <SectionHeader icon={FaUser} title="Billing To" subtitle="Contact the invoice is addressed to" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {billingFields.map(({ key, label, icon: Icon, full }) => (
                            <div key={key} className={full ? "md:col-span-2" : ""}>
                                <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                                <div className="relative">
                                    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                                    <input
                                        className={`${inputClass} pl-9`}
                                        placeholder={label}
                                        value={formData.billTo[key]}
                                        onChange={(e) => handleBillToChange(key, e.target.value)}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Items */}
                <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    <SectionHeader icon={FaBoxOpen} title="Items" subtitle="Existing items are read-only; add or remove as needed" />

                    <div className="mb-5">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Add Item</label>
                        <Select
                            options={itemOptions}
                            value={selectedItemOption}
                            onChange={(opt) => {
                                setSelectedItemOption(opt);
                                handleItemSelect(opt);
                            }}
                            placeholder="Search and select an item to add..."
                            components={{ Option: CustomOption }}
                            styles={selectStyles}
                            isClearable
                            isSearchable
                        />
                    </div>

                    {allItemsCount === 0 ? (
                        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                            <FaBoxOpen className="mx-auto text-3xl text-gray-300 mb-2" />
                            <p className="text-sm font-medium text-gray-500">No items in this order</p>
                            <p className="text-xs text-gray-400 mt-1">Use the selector above to add an item.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-lg border border-gray-200">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                                        <th className="text-left font-semibold px-4 py-3">Item</th>
                                        <th className="text-center font-semibold px-4 py-3 w-24">Qty</th>
                                        <th className="text-right font-semibold px-4 py-3 w-28">Rate</th>
                                        <th className="text-center font-semibold px-4 py-3 w-28">Discount</th>
                                        <th className="text-right font-semibold px-4 py-3 w-32">Total</th>
                                        <th className="text-center font-semibold px-4 py-3 w-16">&nbsp;</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {/* Current Items (read-only rate/qty, editable description, removable) */}
                                    {currentItems.map(item => {
                                        const qty = Number(item.quantity) || 0;
                                        const rate = Number(item.unitPrice || item.rate) || 0;
                                        const discount = Number(item.discount) || 0;
                                        const total = Math.max(qty * rate - discount, 0);

                                        return (
                                            <tr key={item._id} className="hover:bg-gray-50/70 transition">
                                                <td className="px-4 py-3 align-top">
                                                    <div className="font-medium text-gray-800">{item.name}</div>
                                                    <input
                                                        type="text"
                                                        className="mt-1.5 w-full rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 placeholder-gray-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 focus:outline-none"
                                                        placeholder="Add a description (optional)"
                                                        value={
                                                            formData.itemUpdates[item._id] ??
                                                            (item.description || "")
                                                        }
                                                        onChange={(e) =>
                                                            handleExistingDescriptionChange(item._id, e.target.value)
                                                        }
                                                    />
                                                </td>
                                                <td className="px-4 py-3 text-center align-top text-gray-700">{qty}</td>
                                                <td className="px-4 py-3 text-right align-top text-gray-600">৳{rate.toFixed(2)}</td>
                                                <td className="px-4 py-3 text-right align-top text-gray-600">৳{discount.toFixed(2)}</td>
                                                <td className="px-4 py-3 text-right align-top font-semibold text-gray-900">৳{total.toFixed(2)}</td>
                                                <td className="px-4 py-3 text-center align-top">
                                                    <button
                                                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-red-500 hover:bg-red-50 transition"
                                                        onClick={() => handleRemoveCurrentItem(item._id)}
                                                        title="Remove item"
                                                    >
                                                        <FaTrashAlt />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    {/* New Items (fully editable) */}
                                    {newItems.map((it, index) => {
                                        const itemData = items.find(x => x.code === it.code);
                                        const qty = Number(it.quantity) || 0;
                                        const rate = Number(itemData?.unitPrice) || 0;
                                        const discount = Number(it.discount) || 0;
                                        const total = Math.max(qty * rate - discount, 0);

                                        return (
                                            <tr key={`new-${index}`} className="bg-teal-50/40 hover:bg-teal-50/70 transition">
                                                <td className="px-4 py-3 align-top">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium text-gray-800">{itemData?.name || it.code}</span>
                                                        <span className="text-[10px] font-semibold uppercase tracking-wide text-teal-700 bg-teal-100 rounded px-1.5 py-0.5">New</span>
                                                    </div>
                                                    <input
                                                        type="text"
                                                        className="mt-1.5 w-full rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 placeholder-gray-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 focus:outline-none"
                                                        placeholder="Add a description (optional)"
                                                        value={it.description || ""}
                                                        onChange={(e) =>
                                                            handleNewItemChange(index, "description", e.target.value)
                                                        }
                                                    />
                                                </td>
                                                <td className="px-4 py-3 text-center align-top">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        className="w-16 rounded-md border border-gray-300 px-2 py-1 text-center text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 focus:outline-none"
                                                        value={qty}
                                                        onChange={(e) =>
                                                            handleNewItemChange(index, "quantity", +e.target.value)
                                                        }
                                                    />
                                                </td>
                                                <td className="px-4 py-3 text-right align-top text-gray-600">৳{rate.toFixed(2)}</td>
                                                <td className="px-4 py-3 text-center align-top">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        className="w-20 rounded-md border border-gray-300 px-2 py-1 text-center text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 focus:outline-none"
                                                        value={discount}
                                                        onChange={(e) =>
                                                            handleNewItemChange(index, "discount", e.target.value)
                                                        }
                                                    />
                                                </td>
                                                <td className="px-4 py-3 text-right align-top font-semibold text-gray-900">৳{total.toFixed(2)}</td>
                                                <td className="px-4 py-3 text-center align-top">
                                                    <button
                                                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-red-500 hover:bg-red-50 transition"
                                                        onClick={() => handleRemoveNewItem(index)}
                                                        title="Remove item"
                                                    >
                                                        <FaTrashAlt />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                {/* Summary + actions */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <button
                        onClick={handleSubmit}
                        disabled={isUpdating}
                        className="order-2 md:order-1 inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-teal-600 to-blue-500 text-white text-sm font-semibold rounded-lg shadow-md hover:shadow-lg hover:opacity-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isUpdating ? (
                            <>
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Updating...</span>
                            </>
                        ) : (
                            <>
                                <FaSave />
                                <span>Update Order</span>
                            </>
                        )}
                    </button>

                    <div className="order-1 md:order-2 w-full md:w-80 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                        <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-100">
                            <h4 className="text-sm font-semibold text-gray-700">Order Summary</h4>
                        </div>
                        <div className="p-4 space-y-2.5">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Total Quantity</span>
                                <span className="font-medium text-gray-800">{summary.totalQuantity}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Sub Total</span>
                                <span className="font-medium text-gray-800">৳{summary.subTotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Total Discount</span>
                                <span className="font-medium text-red-500">− ৳{summary.totalDiscount.toFixed(2)}</span>
                            </div>
                            <div className="border-t border-gray-100 pt-2.5 flex justify-between items-center">
                                <span className="text-sm font-semibold text-gray-700">Grand Total</span>
                                <span className="text-lg font-bold text-teal-600">৳{summary.grandTotal.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderUpdate;
